import React, { useState, useEffect, useRef } from "react";
import { useRestaurante } from "../context/RestauranteContext";
import { useUser } from "../context/UserContext";
import { useAvaliacao } from "../context/AvaliacaoRestauranteContext";
import { useNavigate } from "react-router-dom";
import "../css/ListarRestaurantes.css";
import CampoBusca from "../components/CampoBusca";
import { Botao, EstadoVazio, ListaSkeleton, SkeletonRestaurante } from "../components/ui";
import { normalizarDia, situacaoFuncionamento } from "../utils/horarioFuncionamento";
import { FaUtensils, FaWifi, FaSearch, FaBox, FaEdit, FaChartBar, FaEllipsisV, FaTimes, FaStar, FaQrcode } from "react-icons/fa";
import Modal from "../components/GenericModal.js";
import QRCodeModal from "../components/Qrcodemodal.js";
import image from "../assets/Gemini_Generated_Image_wjmb1gwjmb1gwjmb.png";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ListarRestaurantes = () => {
  const { restaurantes, buscarRestaurantes, fetchRes, carregandoRestaurantes, erroRestaurantes } = useRestaurante();
  const [termoBusca, setTermoBusca] = useState("");
  // Ao sair da tela, desfaz o filtro para a lista voltar completa.
  useEffect(() => () => buscarRestaurantes(""), []); // eslint-disable-line react-hooks/exhaustive-deps
  const { user, userDecoded } = useUser();
  const { buscarMediaAvaliacoes } = useAvaliacao();
  const navigate = useNavigate();
  const [modalInfo, setModalInfo] = useState(false);
  const [modalQRCode, setModalQRCode] = useState(false);
  const [selectedRestaurante, setSelectedRestaurante] = useState(null);
  const [avaliacoesRestaurante, setAvaliacoesRestaurante] = useState(null);
  const [loadingAvaliacoes, setLoadingAvaliacoes] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Dias da semana para destacar o dia atual
  const diasSemana = ['DOMINGO', 'SEGUNDA-FEIRA', 'TERÇA-FEIRA', 'QUARTA-FEIRA', 'QUINTA-FEIRA', 'SEXTA-FEIRA', 'SÁBADO'];
  const hoje = normalizarDia(diasSemana[new Date().getDay()]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleDropdown = (id, e) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const handleCadastrarProdutos = (id) => {
    navigate(`/restaurantes/${id}/produtos/cadastrar`);
  };

  const handleListarCardapio = (id) => {
    navigate(`/${id}/cardapio`);
  };

  const handleFecharModal = () => {
    setModalInfo(false);
    setSelectedRestaurante(null);
    setAvaliacoesRestaurante(null);
  };

  const handleVisualizarDetalhes = async (restaurante) => {
    setSelectedRestaurante(restaurante);
    setModalInfo(true);
    setOpenDropdown(null);

    // Buscar avaliações do restaurante
    setLoadingAvaliacoes(true);
    try {
      const avaliacoes = await buscarMediaAvaliacoes(restaurante.id);
      setAvaliacoesRestaurante(avaliacoes);
    } catch (error) {
      console.error("Erro ao buscar avaliações:", error);
      setAvaliacoesRestaurante(null);
    } finally {
      setLoadingAvaliacoes(false);
    }
  };

  const handleAvaliar = (id) => {
    navigate(`/restaurantes/${id}/avaliar`);
  };

  const handleGerarQRCode = (restaurante) => {
    setSelectedRestaurante(restaurante);
    setModalQRCode(true);
    setOpenDropdown(null);
  };

  const handleFecharQRCode = () => {
    setModalQRCode(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpenDropdown(null);
  };

  const handleVerAvaliacoes = (id) => {
    navigate(`/restaurantes/${id}/avaliacoes`);
  };

  // Verifica se o restaurante está aberto agora (null = sem horário cadastrado)
  const verificarSeAberto = (operatingHours) => situacaoFuncionamento(operatingHours)?.aberto ?? null;

  const perfisComAcessoEditar = ['DESENVOLVEDOR', 'ADMIN'];
  const perfisComAcessoGeral = ['DESENVOLVEDOR', 'ADMIN', 'GERENTE', 'FUNCIONARIO'];
  const decoded = userDecoded();

  // Renderiza as estrelas
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`star ${i <= fullStars ? 'filled' : ''}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="restaurantes-pagina">
      <section className="restaurantes-hero">
        <h2>Restaurantes Disponíveis</h2>
        <CampoBusca
          valor={termoBusca}
          onChange={(valor) => { setTermoBusca(valor); buscarRestaurantes(valor.trim()); }}
          placeholder="Buscar restaurante"
        />
      </section>
    <div className="listar-restaurantes-container">
      <ToastContainer />

      {carregandoRestaurantes && !restaurantes?.length ? (
        <ListaSkeleton className="restaurantes-grid restaurantes-carregando" quantidade={4} componente={SkeletonRestaurante} />
      ) : erroRestaurantes && !restaurantes?.length ? (
        <EstadoVazio
          icone={<FaWifi />}
          titulo="Não foi possível carregar os restaurantes"
          texto="Verifique sua conexão e tente de novo."
          acao={<Botao onClick={fetchRes}>Tentar de novo</Botao>}
        />
      ) : !restaurantes?.length ? (
        <EstadoVazio
          icone={<FaSearch />}
          titulo={termoBusca.trim() ? "Nenhum restaurante com esse nome" : "Nenhum restaurante disponível"}
          texto={termoBusca.trim() ? "Confira a grafia ou busque por outro nome." : "Volte mais tarde para ver as opções."}
        />
      ) : (
        <ul className="restaurantes-grid">
          {restaurantes?.map((restaurante) => {
            const aberto = verificarSeAberto(restaurante.operatingHours);
            return (
            <li
              key={restaurante.id} 
              className={`restaurante-card ${openDropdown === restaurante.id ? 'dropdown-active' : ''}`}
            >
              <div className="card-content">
                <img
                  src={restaurante?.logoImage ? restaurante?.logoImage : image}
                  alt={`Logo de ${restaurante.nome}`}
                  className="header-image"
                  loading="lazy"
                  decoding="async"
                  width="90"
                  height="90"
                />
                <div className="restaurante-info">
                  <h3>{restaurante.nome}</h3>
                  {aberto !== null && (
                    <span className={`restaurante-status ${aberto ? 'aberto' : 'fechado'}`}>
                      ● {aberto ? 'Aberto agora' : 'Fechado agora'}
                    </span>
                  )}
                  {restaurante.endereco && (
                    <span className="restaurante-address">
                      📍 {restaurante.endereco.rua}, {restaurante.endereco.numero} - {restaurante.endereco.bairro}
                    </span>
                  )}

                  <div className="restaurante-buttons">
                    {/* Botões principais */}
                    <button 
                      className="btn-primary"
                      onClick={() => handleListarCardapio(restaurante.id)}
                    >
                      <FaUtensils aria-hidden="true" /> Cardápio
                    </button>
                  {user ? (
                    <button 
                      className="btn-primary secondary"
                      onClick={() => handleVerAvaliacoes(restaurante.id)}
                    >
                      <FaStar className="icone-estrela" aria-hidden="true" /> Avaliações
                    </button>
                  ) : (
                    <button 
                      className="btn-primary secondary"
                      onClick={() => handleAvaliar(restaurante.id)}
                    >
                      <FaStar className="icone-estrela" aria-hidden="true" /> Avaliar
                    </button>
                  )}

                    {/* Ações rápidas - só aparecem para usuários com acesso */}
                    {user && decoded.perfis.some(perfil => perfisComAcessoGeral.includes(perfil)) && (
                      <>
                        <span className="actions-divider"></span>

                        <div className="quick-actions">
                          <button 
                            className="btn-icon" 
                            title="Cadastrar Produtos"
                            onClick={() => handleCadastrarProdutos(restaurante.id)}
                          >
                            <FaBox />
                          </button>

                          {decoded.perfis.some(perfil => perfisComAcessoEditar.includes(perfil)) && (
                            <button 
                              className="btn-icon" 
                              title="Editar"
                              onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/restaurantes/editar`)}
                            >
                              <FaEdit />
                            </button>
                          )}

                          <button 
                            className="btn-icon" 
                            title="Relatórios"
                            onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/relatorios`)}
                          >
                            <FaChartBar />
                          </button>

                          <button 
                            className="btn-icon" 
                            title="Ver detalhes"
                            onClick={() => handleVisualizarDetalhes(restaurante)}
                          >
                            <FaSearch />
                          </button>
                        </div>

                        {/* Dropdown menu */}
                        <div className="dropdown" ref={openDropdown === restaurante.id ? dropdownRef : null}>
                          <button 
                            className="dropdown-toggle"
                            onClick={(e) => handleToggleDropdown(restaurante.id, e)}
                          >
                            <FaEllipsisV />
                          </button>

                          {openDropdown === restaurante.id && (
                            <div className="dropdown-menu">
                              <div className="dropdown-section">
                                <div className="dropdown-section-title">Operações</div>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/restaurantes/rotinas`)}
                                >
                                  <span className="icon">📅</span> Minhas Rotinas
                                </button>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/restaurantes/aproveitamentos`)}
                                >
                                  <span className="icon">♻️</span> Aproveitamento
                                </button>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/restaurantes/producao`)}
                                >
                                  <span className="icon">🏭</span> Produção
                                </button>
                              </div>
                              <div className="dropdown-section">
                                <div className="dropdown-section-title">Financeiro</div>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/fornecedores`)}
                                >
                                  <span className="icon">🚚</span> Fornecedores
                                </button>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleNavigate(`/restaurantes/${restaurante.id}/notas`)}
                                >
                                  <span className="icon">🧾</span> Notas
                                </button>
                              </div>
                              <div className="dropdown-section">
                                <div className="dropdown-section-title">Marketing</div>
                                <button 
                                  className="dropdown-item"
                                  onClick={() => handleGerarQRCode(restaurante)}
                                >
                                  <FaQrcode className="icon" /> Gerar QR Code de Avaliação
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Se não tem acesso, mostra só o botão de detalhes */}
                    {(!user || !decoded.perfis.some(perfil => perfisComAcessoGeral.includes(perfil))) && (
                      <button 
                        className="btn-detalhes"
                        aria-label={`Ver detalhes de ${restaurante.nome}`}
                        onClick={() => handleVisualizarDetalhes(restaurante)}
                      >
                        <FaSearch /> Detalhes
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}

      {/* Modal de Detalhes - Novo Design */}
      <Modal isOpen={modalInfo} onClose={handleFecharModal} className="modal-detalhes">
        <div className="modal-detalhes-content">
          {/* Header com imagem */}
          <div className="modal-header-elegante">
            {selectedRestaurante?.operatingHours && (
              <span className={`status-badge-modal ${verificarSeAberto(selectedRestaurante.operatingHours) ? '' : 'closed'}`}>
                ● {verificarSeAberto(selectedRestaurante.operatingHours) ? 'Aberto agora' : 'Fechado'}
              </span>
            )}
            <img
              src={selectedRestaurante?.logoImage || image}
              alt="Imagem do Restaurante"
              className="modal-header-image"
            />
          </div>

          {/* Body */}
          <div className="modal-body-elegante">
            <h2 className="restaurant-name-modal">
              {selectedRestaurante?.nome || "Nome do Restaurante"}
            </h2>
            <p className="restaurant-category-modal">
              🍽️ Restaurante
            </p>

            {/* Card de Avaliações */}
            <div className="rating-card-modal">
              {loadingAvaliacoes ? (
                <p className="loading-avaliacoes">Carregando avaliações...</p>
              ) : avaliacoesRestaurante?.totalAvaliacoes > 0 ? (
                <>
                  <div className="rating-general">
                    <span className="rating-number">
                      {avaliacoesRestaurante.mediaGeral?.toFixed(1) || '0.0'}
                    </span>
                    <div className="rating-stars">
                      <div className="stars-row">
                        {renderStars(avaliacoesRestaurante.mediaGeral || 0)}
                      </div>
                      <span className="rating-count">
                        {avaliacoesRestaurante.totalAvaliacoes || 0} {(avaliacoesRestaurante.totalAvaliacoes || 0) === 1 ? "avaliação" : "avaliações"}
                      </span>
                    </div>
                  </div>

                  <div className="rating-categories">
                    <div className="rating-category-item">
                      <span className="category-icon">🏠</span>
                      <div className="category-info">
                        <div className="category-name">Ambiente</div>
                        <div className="category-bar">
                          <div 
                            className="category-bar-fill" 
                            style={{ width: `${(avaliacoesRestaurante.mediaAmbiente || 0) * 20}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="category-value">
                        {avaliacoesRestaurante.mediaAmbiente?.toFixed(1) || '0.0'}
                      </span>
                    </div>

                    <div className="rating-category-item">
                      <span className="category-icon">🍽️</span>
                      <div className="category-info">
                        <div className="category-name">Comida</div>
                        <div className="category-bar">
                          <div 
                            className="category-bar-fill" 
                            style={{ width: `${(avaliacoesRestaurante.mediaComida || 0) * 20}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="category-value">
                        {avaliacoesRestaurante.mediaComida?.toFixed(1) || '0.0'}
                      </span>
                    </div>

                    <div className="rating-category-item">
                      <span className="category-icon">👨‍🍳</span>
                      <div className="category-info">
                        <div className="category-name">Atendimento</div>
                        <div className="category-bar">
                          <div 
                            className="category-bar-fill" 
                            style={{ width: `${(avaliacoesRestaurante.mediaAtendimento || 0) * 20}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="category-value">
                        {avaliacoesRestaurante.mediaAtendimento?.toFixed(1) || '0.0'}
                      </span>
                    </div>

                    <div className="rating-category-item">
                      <span className="category-icon">💰</span>
                      <div className="category-info">
                        <div className="category-name">Custo-Benefício</div>
                        <div className="category-bar">
                          <div 
                            className="category-bar-fill" 
                            style={{ width: `${(avaliacoesRestaurante.mediaPreco || 0) * 20}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="category-value">
                        {avaliacoesRestaurante.mediaPreco?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="sem-avaliacoes">Nenhuma avaliação ainda. Seja o primeiro a avaliar!</p>
              )}
            </div>

            {/* Endereço */}
            <div className="info-section">
              <div className="info-section-title">Endereço</div>
              <div className="address-card">
                <span className="address-icon">📍</span>
                <div className="address-text">
                  {selectedRestaurante?.endereco ? (
                    <>
                      {selectedRestaurante.endereco.rua}, {selectedRestaurante.endereco.numero}<br />
                      {selectedRestaurante.endereco.bairro}, {selectedRestaurante.endereco.cidade} - {selectedRestaurante.endereco.estado}<br />
                      CEP: {selectedRestaurante.endereco.cep}
                    </>
                  ) : (
                    "Endereço não informado"
                  )}
                </div>
              </div>
            </div>

            {/* Horários */}
            <div className="info-section">
              <div className="info-section-title">Horários</div>
              {selectedRestaurante?.operatingHours?.length > 0 ? (
                <div className="horarios-grid">
                  {selectedRestaurante.operatingHours.map((hora) => (
                    <div 
                      key={hora.id} 
                      className={`horario-item ${normalizarDia(hora.diaSemana) === hoje ? 'hoje' : ''}`}
                    >
                      <span className="horario-dia">
                        {hora.diaSemana}
                        {normalizarDia(hora.diaSemana) === hoje && ' (Hoje)'}
                      </span>
                      <span className="horario-horas">
                        {hora.horarioAbertura} - {hora.horarioFechamento}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="sem-horario">Horário não informado</p>
              )}
            </div>

            {/* Ações */}
            <div className="modal-actions-elegante">
              <button className="btn-modal secondary" onClick={handleFecharModal}>
                Fechar
              </button>
              <button 
                className="btn-modal primary" 
                onClick={() => {
                  handleFecharModal();
                  handleAvaliar(selectedRestaurante?.id);
                }}
              >
                <FaStar /> Avaliar
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal de QR Code */}
      <QRCodeModal 
        isOpen={modalQRCode} 
        onClose={handleFecharQRCode} 
        restaurante={selectedRestaurante}
      />
    </div>
    </div>
  );
};

export default ListarRestaurantes;